<template>
  <van-popover 
    class="ob-popover"
    :class="[
      fitWidth && 'fit-width' ,
      !!customColor && 'customColor',
      className && className
    ]"
    v-model:show="visible" 
    :placement="placement"
    :show-arrow="showArrow"
    :offset="fitWidth ? [0, -1] : offset"
    :overlay="showOverlay || fitWidth"
    :overlay-style="overLayStyle"
    :style="{
      '--custom-popover-color': customColor,
      '--custom-popover-radius': customRadius,
      '--van-popover-arrow-size': arrowSize,
    }"
    @closed="afterClose"
  >
    <template #reference>
      <div 
        class="ob-popover-content" 
        ref="popContentRef" 
        @mousemove="onMouseMove"
      >
        <slot name="prefix"></slot>
        <slot></slot>
        <slot name="suffix"></slot>
      </div>
    </template>
    
    <slot name="custom">
      <div
        v-if="popContentType === 'menu'"
        class="ob-popover-menu"
        ref="popMenuRef"
        @mousemove="onMouseMove"
        :style="{
          width: `${menuWidth}px`
        }"
      >
        <slot name="menu">
          <p 
            v-for="(item, index) in actions"
            :key="index"
            :class="{
              active: showSelected && (selectedItem.value || defaultValue) === item.value,
              hasline: item.hasline
            }"
            @click="onChange(item)"
          >
            <span 
              class="ob-popover-menu__label" 
              :style="{
                width: labelMaxWidth ? `${labelMaxWidth}px` : 'auto'
              }"
              :ref="(el) => { !!el && (labelRefs[index] = el) }"
            >
              {{ item.label }}
            </span>
            <SvgIcon v-show="showSelected && (selectedItem.value || defaultValue) === item.value" name="icon-done"/>
          </p>
        </slot>
      </div>
    </slot>
  </van-popover>
  <teleport to="body">
      <van-overlay v-if="fitWidth" :show="visible" @click="visible = false" :custom-style="{
      top: `${layerOffset + 1}px`
    }" />
  </teleport>
</template>

<script setup>

import { useSystemStore } from '@/stores/system'
import { storeToRefs } from 'pinia';
import { onBeforeRouteUpdate } from 'vue-router';

const props = defineProps({
  defaultValue: {
    type: [String, Number],
    default: ''
  },
  actions: {
    type: Array,
    default: () => []
  },
  offset: {
    type: Array,
    default: () => [0, 8]
  },
  overLayStyle: {
    type: Object,
    default: () => {
      return {
        backgroundColor: 'transparent'
      }
    }
  },
  placement: {
    type: String,
    default: 'bottom'
  },
  showSelected: {
    type: Boolean,
    default: false
  },
  showOverlay: {
    type: Boolean,
    default: false
  },
  closeOnClickAction: {
    type: Boolean,
    default: true
  },
  fitWidth: {
    type: Boolean,
    default: false
  },
  showArrow: {
    type: Boolean,
    default: false
  },
  customColor: {
    type: String,
    default: ''
  },
  customRadius: {
    type: String,
    default: ''
  },
  trigger: {
    type: String,
    default: 'click'
  },
  popContentType: {
    type: String,
    default: 'menu'
  },
  arrowSize: {
    type: String,
    default: ''
  },
  className: {
    type: [String, Array],
    default: ''
  },
  zIndex: {
    type: [String, Number],
    default: ''
  },
})

const emits = defineEmits(['change', 'shown'])
const systemStore = useSystemStore()
const { isWeb } = storeToRefs(systemStore)

const labelRefs = ref([])
const popMenuRef = ref(null)
const popContentRef = ref(null)

const checkIsout = ref(null)
const visible = ref(false)
const layerOffset = ref(0)
const labelMaxWidth = ref(null)

const selectedItem = ref({})

const menuWidth = computed(() => {
  let width = labelMaxWidth.value ? `${labelMaxWidth.value + ((isWeb.value ? 24 : 16) * 2) + (props.showSelected ? (24 + 8) : 0)}px` : 'auto'
  return props.fitWidth ? popContentRef.value.offsetWidth : width
})

function onChange (value) {
  visible.value = !props.closeOnClickAction
  selectedItem.value = value
  emits('change', value)
}

function getMenuLabelWidth () {
  const labelWidthList = labelRefs.value.map(i => i.offsetWidth)
  const maxLabelWidth = Math.max.apply(null, labelWidthList)
  if (maxLabelWidth) {
    labelMaxWidth.value = maxLabelWidth
  }
  layerOffset.value = popContentRef.value.getBoundingClientRect().top + popContentRef.value.offsetHeight
}

function afterClose () {
  labelMaxWidth.value = null
}

// hover处理
function onMouseMove (event) {
  if (props.trigger !== 'hover') return
  const mouseX = event.clientX;
  const mouseY = event.clientY;

  const contentRect = popContentRef.value?.getBoundingClientRect() || {}
  const menutRect = popMenuRef.value?.getBoundingClientRect() || {}
  const inContentElement = (
    mouseX >= (contentRect.left) &&
    mouseX <= (contentRect.right) &&
    mouseY >= (contentRect.top) &&
    mouseY <= (contentRect.bottom)
  )
  const inMenuElement = (
    mouseX >= (menutRect.left) &&
    mouseX <= (menutRect.right) &&
    mouseY >= (menutRect.top) &&
    mouseY <= (menutRect.bottom)
  );
  if (inContentElement || inMenuElement) {
    visible.value = true
    clearTimeout(checkIsout.value)
    checkIsout.value = null
  } else {
    if (checkIsout.value) return
    checkIsout.value = setTimeout(() => {
      if (!inContentElement && !inMenuElement) {
        visible.value = false
        clearTimeout(checkIsout.value)
        checkIsout.value = null
      }
    }, 200);
  }
}

watch(labelRefs, (val) => {
  if (!val?.length) return
  getMenuLabelWidth()
}, {
  immediate: true,
  deep: true
})

watch(visible, (val) => {
  if (val) {
    props.trigger && document.body.addEventListener('mousemove', onMouseMove)
    nextTick(() => {
      getMenuLabelWidth()
    })
  } else {
    props.trigger && document.body.removeEventListener('mousemove', onMouseMove)
  }
  emits('shown', val)
}, {
  immediate: true,
})

onBeforeRouteLeave(() => {
  visible.value = false
  selectedItem.value = {}
})


defineExpose({
  visible
})
</script>

<style lang="postcss" scoped>
.ob-popover-content {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.ob-popover-menu {
  padding: 8px 0;
  border-radius: var(--radius-xs);
  box-shadow: 0 2px 12px rgba(50, 50, 51, .12);
  background-color: var(--color-background-primary);
  overflow: hidden;
  p {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    min-height: 24px;
    font-size: 14px;
    padding: 11px 24px;
    cursor: pointer;
    &:hover {
      background-color: var(--color-background-secondary);
    }
  }
  .ob-popover-menu__label {
    flex-shrink: 0;
    white-space: nowrap;
  }
  .active  {
    color: var(--color-brandSecondary-community);
  }
  .icon-done {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: var(--color-brandSecondary-community);
    margin-left: 8px;
  }
}
.fit-width {
  .ob-popover-menu {
    box-shadow: none;
    border-radius: 0;
    p {
      padding: 10px 24px;
    }
  }
}
@media screen and (max-width: 1023px) {
  .ob-popover-menu {
    p {
      height: 40px;
      font-size: 14px;
      padding: 10px 16px;
      box-sizing: border-box;
      cursor: default;
    }
  }
}
</style>

<style lang="postcss">
.ob-popover {
  .van-popover__content {
    border-radius: 0;
    background-color: transparent;
    overflow: hidden;
    color: var(--color-text-primary);
  }
}
.fit-width {
  .van-popover__content {
    box-shadow: none;
  }
}
.customColor {
  .van-popover__arrow {
    --van-popover-arrow-size: 8px !important;
    color: var(--custom-popover-color, transparent) !important;
    bottom: 1px !important;
  }
  .van-popover__content {
    border-radius: var(--custom-popover-radius);
    background-color: var(--custom-popover-color);
    overflow: hidden;
  }
}
.hasline{
  border-bottom: 1px solid #f0f0f0;
}
</style>